import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Popover with a checkbox list of display buckets. Tracks a draft selection
 * while open so toggling a checkbox doesn't immediately mutate the page state;
 * the parent only sees the committed selection when "Apply" is clicked.
 *
 * Buckets are the user-facing display labels (Pending / Confirmed / etc.).
 * The page is responsible for expanding them back into canonical statuses
 * (via `expandBuckets` in `StatusPill.jsx`).
 */
export function StatusFilter({ buckets, selected, onChange }) {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState(selected);

	const toggle = (b) => {
		setDraft((d) => (d.includes(b) ? d.filter((x) => x !== b) : [...d, b]));
	};

	return (
		<Popover
			open={open}
			onOpenChange={(v) => {
				setOpen(v);
				if (v) setDraft(selected);
			}}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
				>
					Order status
					<ChevronDown className="h-4 w-4" />
					{selected.length > 0 ? (
						<span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground px-1.5 text-[11px] font-bold text-primary">
							{selected.length}
						</span>
					) : null}
				</button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-64 p-3">
				<div className="space-y-2">
					{buckets.map((b) => (
						<label key={b} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted">
							<input
								type="checkbox"
								checked={draft.includes(b)}
								onChange={() => toggle(b)}
								className="h-4 w-4 cursor-pointer rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
							/>
							<span className="text-sm font-medium text-foreground">{b}</span>
						</label>
					))}
				</div>
				<div className="mt-2 flex items-center justify-between border-t border-border/60 pt-3">
					<button type="button" onClick={() => setDraft([])} className="text-sm font-semibold text-primary hover:underline">
						Reset
					</button>
					<button
						type="button"
						onClick={() => {
							onChange(draft);
							setOpen(false);
						}}
						className="h-9 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
					>
						Apply
					</button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
